import { useEventListener, useRafFn } from '@vueuse/core'

interface UseDragAutoscrollOptions {
  topBoundary?: () => number
  bottomBoundary?: () => number
  zone?: number
  dwellMs?: number
  maxSpeed?: number
  minSpeed?: number
  damping?: number
}

export interface DragLikeEvent {
  from?: HTMLElement
}

/**
 * A Vue composable that scrolls the dragged element's nearest scrollable
 * ancestor while a drag-and-drop interaction is in progress and the cursor
 * dwells near the top or bottom of the visible viewport. Designed to be
 * driven by `vuedraggable`'s `@start` / `@end` events, but works with any
 * source that supplies an `event.from` HTMLElement on drag start.
 *
 * @param options.topBoundary - Y coord of the visible top edge (e.g. the
 *   bottom of a sticky header). Defaults to 0 (window top).
 * @param options.bottomBoundary - Y coord of the visible bottom edge.
 *   Defaults to `window.innerHeight`.
 * @param options.zone - Pixels from each boundary that count as the
 *   trigger zone. Defaults to 80.
 * @param options.dwellMs - The cursor must stay inside the trigger zone
 *   for this long before scrolling starts; prevents in-flight drags
 *   from triggering scroll while passing through. Defaults to 150.
 * @param options.maxSpeed - Cap on per-frame scroll delta in pixels.
 * @param options.minSpeed - Floor on per-frame scroll delta in pixels.
 * @param options.damping - Larger values = slower scroll for the same
 *   distance from the boundary. Defaults to 4.
 *
 * @returns `onDragStart` / `onDragEnd` handlers to wire to drag events.
 *   Pointer listener and animation frame are torn down automatically on
 *   component unmount.
 */
export default function useDragAutoscroll(
  options: UseDragAutoscrollOptions = {}
): {
  onDragStart: (event?: DragLikeEvent) => void
  onDragEnd: () => void
} {
  const {
    topBoundary = (): number => 0,
    bottomBoundary = (): number => window.innerHeight,
    zone = 80,
    dwellMs = 150,
    maxSpeed = 18,
    minSpeed = 2,
    damping = 4,
  } = options

  let pointerY = 0
  let target: Element | null = null
  let zoneEnteredAt: number | null = null

  const findScrollableParent = (
    element: HTMLElement | null | undefined
  ): Element => {
    let parent = element?.parentElement
    while (parent) {
      const style = window.getComputedStyle(parent)
      if (
        ['auto', 'scroll'].includes(style.overflowY) &&
        parent.scrollHeight > parent.clientHeight
      ) {
        return parent
      }
      parent = parent.parentElement
    }
    return document.scrollingElement || document.documentElement
  }

  const tick = (): void => {
    const top = topBoundary()
    const bottom = bottomBoundary()
    let delta = 0
    let inZone = false

    if (pointerY < top + zone) {
      inZone = true
      const distance = top + zone - pointerY
      delta = -Math.min(maxSpeed, Math.max(minSpeed, distance / damping))
    } else if (pointerY > bottom - zone) {
      inZone = true
      const distance = pointerY - (bottom - zone)
      delta = Math.min(maxSpeed, Math.max(minSpeed, distance / damping))
    }

    if (!inZone) {
      zoneEnteredAt = null
    } else if (zoneEnteredAt === null) {
      zoneEnteredAt = performance.now()
    }

    const dwelled =
      zoneEnteredAt !== null && performance.now() - zoneEnteredAt >= dwellMs

    if (delta !== 0 && dwelled && target) {
      target.scrollBy(0, delta)
    }
  }

  const raf = useRafFn(tick, { immediate: false })

  useEventListener(document, 'pointermove', (event: PointerEvent) => {
    pointerY = event.clientY
  })

  const onDragStart = (event?: DragLikeEvent): void => {
    target = findScrollableParent(event?.from)
    pointerY = 0
    zoneEnteredAt = null
    raf.resume()
  }

  const onDragEnd = (): void => {
    raf.pause()
    target = null
    zoneEnteredAt = null
  }

  return { onDragStart, onDragEnd }
}
